import { useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  getIsAIGeneratedSection,
  getIsAIGeneratedSectionGroup,
  getLeadFormDraftFingerprint,
} from "../utils/aiGenerated.js";

const UNUSED_VALUE = "__aiGeneratedUnusedValue";
const UNUSED_HASH = "__aiGeneratedUnusedHash";
const UNUSED_VERSION = "__aiGeneratedUnusedVersion";

// `control` must be provided by a caller that already verified a form context
// exists (see ChipAIGeneratedSection). Without it, useWatch would read a null
// FormContext and crash the subtree.
export const useAIGeneratedSection = ({ control, basePath, section } = {}) => {
  const hasSection = Boolean(control && basePath && section);
  const value = useWatch({
    control,
    name: hasSection ? `${basePath}.${section}` : UNUSED_VALUE,
  });
  const storedHash = useWatch({
    control,
    name: hasSection
      ? `${basePath}.aiGenerated.sections.${section}`
      : UNUSED_HASH,
  });
  const version = useWatch({
    control,
    name: hasSection ? `${basePath}.aiGenerated.v` : UNUSED_VERSION,
  });

  return useMemo(() => {
    if (!hasSection) return false;
    return getIsAIGeneratedSection({
      entity: {
        aiGenerated: {
          v: version,
          sections: { [section]: storedHash },
        },
        [section]: value,
      },
      section,
      value,
    });
  }, [hasSection, section, storedHash, value, version]);
};

export const useAIGeneratedAnySection = ({ basePath, sections = [] } = {}) => {
  const { control } = useFormContext();
  const hasBasePath = Boolean(basePath);
  const entity = useWatch({
    control,
    name: hasBasePath ? basePath : "__aiGeneratedUnusedEntity",
  });
  const sectionList = useMemo(
    () =>
      Array.isArray(sections)
        ? sections.filter((section) => typeof section === "string" && section)
        : [],
    [sections]
  );

  return useMemo(() => {
    if (!hasBasePath || sectionList.length === 0 || !entity) return false;
    return getIsAIGeneratedSectionGroup({
      entity,
      sections: sectionList,
    });
  }, [entity, hasBasePath, sectionList]);
};

export const useAIGeneratedLeadFormDraft = ({
  enabled = false,
  control,
  getValues,
}) => {
  const draft = useWatch({ control });
  const initialFingerprintRef = useRef(null);

  if (enabled && initialFingerprintRef.current === null && getValues) {
    initialFingerprintRef.current = getLeadFormDraftFingerprint(getValues());
  }

  return useMemo(() => {
    if (!enabled || !initialFingerprintRef.current) return false;
    return getLeadFormDraftFingerprint(draft) === initialFingerprintRef.current;
  }, [draft, enabled]);
};
